import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { UserLogin, UserActive, FetchCSRFCookie } from "api/rpc";

import { initialize, userLogin } from "redux/actions/auth";
import { showError } from "redux/actions/ui";
import { UserType } from "redux/reducers/models/user";
import { useAppDispatch } from "hooks/redux";
import useKeyPress from "../../hooks/events/useKeyPress";

import Input from "components/input/Input";
import { ButtonNew as Button } from "components/buttonNew/index";
import { useGeolocation } from "hooks/useGeoLocation/useGeoLocation";

interface ILoginState {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { getLocation } = useGeolocation();

  const dispatch = useAppDispatch();
  const enterPressed = useKeyPress(["Enter"]);

  const [loginState, setLoginState] = useState<ILoginState>({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (enterPressed) {
      void handleLogin();
    }
  }, [enterPressed]);

  async function handleLogin(e?: React.MouseEvent<HTMLElement, MouseEvent>) {
    if (e) {
      e.preventDefault();
    }
    const fetchXSRFTokenRes = await FetchCSRFCookie(true);

    if (fetchXSRFTokenRes.status !== 204) {
      return;
    }

    if (loginState.email == "" || loginState.password == "") {
      // covers all falsey cases? (null, undefined, ...)
      dispatch(showError(t("Username and/or password cannot be blank.")));
      return;
    }

    const location = await getLocation();

    const loginRes = await UserLogin(
      {
        email: loginState.email,
        password: loginState.password,
        admin: true,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
      },
      true,
    );

    if (loginRes.status === StatusCode.UNAUTHORIZED) {
      dispatch(showError(t("Username and/or password is incorrect")));
    }

    if (loginRes.status !== StatusCode.OK) {
      dispatch(showError(typeof loginRes?.data?.message === "string" ? loginRes?.data?.message : t("Error logging in as user")));
      return;
    }

    if (loginRes.data.user.user_type === UserType.STANDARD_CUSTOMER) {
      dispatch(showError(t("User type not allowed")));
      return;
    } else {
      const activeUserRes = await UserActive(true);
      if (activeUserRes.status === StatusCode.OK) {
        dispatch(userLogin(activeUserRes.data));
        dispatch(initialize(true));
        if (activeUserRes.data.permissions?.kiosk_view) {
          history.replace("/admin/kiosk");
        } else {
          history.push("/admin");
        }
      } else {
        dispatch(showError(t("Error logging in as user")));
      }
    }
  }

  return (
    <div>
      <div className="login-main-content login-left">
        <div className="login-container">
          <div className="login-content">
            <img className="login-logo" src="../../../public/images/Tee-On.png" alt={t("Tee-On Logo")} />
            <div className="login-content--header">
              <p>
                <span>{t("Log in")}</span>
              </p>
              <p className="text-subdued welcome-back-title">
                {t("Welcome back! Please log in to access your course admin")}
              </p>
            </div>
            <div className="login-content--form">
              <label>{t("Email")}</label>
              <Input
                className="w-full mb-4"
                placeholder={t("Enter your email")}
                name="email"
                value={loginState.email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setLoginState(prev => ({ ...prev, email: e.target.value }))
                }
              />

              <label>{t("Password")}</label>
              <Input
                className="w-full mb-4"
                placeholder={t("Password")}
                name="password"
                type="password"
                value={loginState.password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setLoginState(prev => ({ ...prev, password: e.target.value }))
                }
              />

              <Button type="primary" onClick={e => void handleLogin(e)} block>
                {t("Sign in")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="login-main-content login-right">
        <h2>{t("All-in-one golf course management software")}</h2>
        <p className="mt-4">{t("Sign into your account and get access to all features")}</p>
      </div>
    </div>
  );
};

export default Login;
